import React, { useState, useCallback } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/core/styles';
import styles from './styles';

const useStyles = makeStyles(styles);

const InfoTooltip = ({
  content,
  placement,
  iconSize,
  variant,
  className,
  contentClassName,
  icon,
  interactive,
  surface,
  size,
}) => {
  const classes = useStyles();
  // Touch devices: tap-to-toggle via controlled `open` + ClickAwayListener.
  // Pointer devices: hover/focus drive `open` via onOpen/onClose.
  const isTouch = useMediaQuery('(hover: none)');
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const handleIconClick = useCallback((e) => {
    if (!isTouch) return;
    e.stopPropagation();
    setOpen((prev) => !prev);
  }, [isTouch]);

  if (content === null || content === undefined || content === false) return null;

  const sizeClass = iconSize === 'small' ? classes.iconSmall : classes.iconMedium;
  const triggerClass = classnames(
    icon ? classes.customIcon : classnames(classes.infoIcon, sizeClass),
    className
  );

  if (variant === 'inline') {
    const containerHandlers = isTouch
      ? {}
      : { onMouseEnter: handleOpen, onMouseLeave: handleClose, onFocus: handleOpen, onBlur: handleClose };

    const inlineNode = (
      <div className={classes.inlineContainer} {...containerHandlers}>
        <span
          className={triggerClass}
          onClick={handleIconClick}
          tabIndex={0}
          role="button"
          aria-label="info"
        >
          {icon || 'i'}
        </span>
        {open && (
          <div className={classnames(classes.inlineHint, contentClassName)}>
            {content}
          </div>
        )}
      </div>
    );

    if (!isTouch) return inlineNode;
    return <ClickAwayListener onClickAway={handleClose}>{inlineNode}</ClickAwayListener>;
  }

  const tooltipNode = (
    <Tooltip
      title={content}
      placement={placement}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disableHoverListener={isTouch}
      disableTouchListener
      interactive={interactive}
      classes={{
        tooltip: classnames(
          classes.hint,
          size === 'wide' && classes.hintWide,
          surface === 'light' && classes.hintLight
        ),
      }}
    >
      <span className={triggerClass} onClick={handleIconClick}>
        {icon || 'i'}
      </span>
    </Tooltip>
  );

  if (!isTouch) return tooltipNode;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      {tooltipNode}
    </ClickAwayListener>
  );
};

InfoTooltip.propTypes = {
  content: propTypes.node,
  placement: propTypes.oneOf([
    'top', 'top-start', 'top-end',
    'bottom', 'bottom-start', 'bottom-end',
    'left', 'left-start', 'left-end',
    'right', 'right-start', 'right-end',
  ]),
  iconSize: propTypes.oneOf(['small', 'medium']),
  variant: propTypes.oneOf(['popover', 'inline']),
  className: propTypes.string,
  contentClassName: propTypes.string,
  icon: propTypes.node,
  interactive: propTypes.bool,
  surface: propTypes.oneOf(['dark', 'light']),
  size: propTypes.oneOf(['default', 'wide']),
};

InfoTooltip.defaultProps = {
  content: null,
  placement: 'bottom',
  iconSize: 'medium',
  variant: 'popover',
  className: undefined,
  contentClassName: undefined,
  icon: null,
  interactive: false,
  surface: 'dark',
  size: 'default',
};

export default InfoTooltip;
